import React, { useState, useContext, useEffect, useRef } from 'react'
import './sidebar.scss'
import { WorkspaceContext } from '../WorkspaceContext'
import { useParams } from 'react-router-dom'
import FileExplorerTab from './Tabs/FileExplorerTab'
import FileListTab from './Tabs/FileListTab'
import MemberTab from './Tabs/MemberTab'
import SunIcon from './SunIcon'
import MoonIcon from './MoonIcon'
import MemberRenameInput from './MemberRenameInput'
import SettingsTab from '../settings/SettingsTab'
import api from '../../../api'
import JoinRoomIcon from '../../../shared/components/svgIcons/JoinRoomIcon'
import ExpandIcon from '../../../shared/components/svgIcons/ExpandIcon'
import PlusIcon from '../PlusIcon'
import type { SBmodeTabs } from '../WorkspaceContext'
import UiModeToggle from './UiModeToggle'
import RenameIcon from '../../../shared/components/svgIcons/RenameIcon'
import MenuIcon from '../../../shared/components/svgIcons/MenuIcon'
import ViewsIcon from '../../../shared/components/svgIcons/ViewsIcon'
import { formatCompactCount } from '../../../shared/utils/formatCompactCount'

function SideBar() {

    const ctx = useContext(WorkspaceContext);
    if (!ctx) throw new Error("Workspace must be wrapped in WorkspaceContext.Provider");
    const { ctheme, setcTheme, toggleSideBarOpen, memberDetails, roomDetails, roomSettings, roomStorage, membersList, setLimitMessage, setLimitPopup, setAddNewPopup, sbMode, uiMode, setSbMode, setUiMode, setQrPopup } = ctx;

    const { roomId } = useParams();
    const clientId = localStorage.getItem("flashroom_id");


    const [sidebar, setSideBar] = useState(true);
    const mobileSheetDragRef = useRef<{ startY: number; startX: number } | null>(null);


    const setModeClick = (m: SBmodeTabs) => {
        setSbMode(m);

        if (m === 'editor') {
            setUiMode('editor')
        } else if (m === 'filelist') {
            setUiMode('files')
        }


    }

    useEffect(() => {
        if (!roomSettings.trackMembersEnabled && sbMode === 'members') {
            setSbMode(uiMode === 'files' ? 'filelist' : 'editor');
        }
    }, [roomSettings.trackMembersEnabled, sbMode, setSbMode, uiMode]);

    useEffect(() => {
        setSbMode(prev => {
            if (uiMode === 'files' && prev === 'editor') return 'filelist';
            if (uiMode === 'editor' && prev === 'filelist') return 'editor';
            return prev;
        });
    }, [setSbMode, uiMode]);

    const toggleSideBar = () => {
        if (sidebar) {
            setSideBar(false)
            toggleSideBarOpen(false)
        } else {
            setSideBar(true);
            toggleSideBarOpen(true);
        }

    }

    const mobileSheetPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        if (event.button !== 0) return;

        mobileSheetDragRef.current = {
            startY: event.clientY,
            startX: event.clientX,
        };

        const handlePointerMove = (moveEvent: PointerEvent) => {
            if (!mobileSheetDragRef.current) return;
            const deltaY = moveEvent.clientY - mobileSheetDragRef.current.startY;
            const deltaX = Math.abs(moveEvent.clientX - mobileSheetDragRef.current.startX);

            if (deltaY > 72 && deltaY > deltaX) {
                setSideBar(false);
                toggleSideBarOpen(false);
                cleanup();
            }
        };

        const handlePointerUp = (upEvent: PointerEvent) => {
            if (!mobileSheetDragRef.current) return;

            const deltaY = upEvent.clientY - mobileSheetDragRef.current.startY;
            const deltaX = Math.abs(upEvent.clientX - mobileSheetDragRef.current.startX);

            if (Math.abs(deltaY) < 8 && deltaX < 8) {
                toggleSideBar();
            } else if (deltaY > 42 && deltaY > deltaX) {
                setSideBar(false);
                toggleSideBarOpen(false);
            }

            cleanup();
        };

        const cleanup = () => {
            mobileSheetDragRef.current = null;
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
            window.removeEventListener('pointercancel', cleanup);
        };

        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);
        window.addEventListener('pointercancel', cleanup);
    };

    // On mobile the sidebar renders as a bottom sheet that should start
    // closed (the editor is the default view). Desktop keeps its default
    // open state, so this only forces it shut the first time a session
    // loads below the mobile breakpoint.
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 600px)');

        const closeForMobile = (matches: boolean) => {
            if (!matches) return;
            setSideBar(false);
            toggleSideBarOpen(false);
        };

        closeForMobile(mq.matches);

        const handleChange = (e: MediaQueryListEvent) => closeForMobile(e.matches);
        mq.addEventListener('change', handleChange);
        return () => mq.removeEventListener('change', handleChange);
    }, []);

    const mobileModeClick = (mode: SBmodeTabs) => {
        if (sidebar && sbMode === mode) {
            setSideBar(false);
            toggleSideBarOpen(false);
        } else {
            setModeClick(mode);
            setSideBar(true);
            toggleSideBarOpen(true);
        }
    }
    const [rename, setRename] = useState(false);

    const cancelRename = () => {
        setRename(false);
    };

    const commitRename = async (nextName: string) => {
        try {
            await api.patch(`/rooms/${roomId}/members/${clientId}/rename`, {
                rename: nextName
            });

            setRename(false);
        } catch (error) {
            setLimitMessage('Could not change your name. Please try again.');
            setLimitPopup(true);
        }
    };


    const activeMembers = membersList.filter((m) => m.isActive);


    const calculateStorageInMb = (storage: number) => {
        if (Math.floor(storage / (1024 * 1024)) === 0) {
            return `${Math.floor(storage / 1024)} KB`;
        }

        const mb = storage / (1024 * 1024);
        // Drop the decimal once it rounds up to the 100 MB cap — "100.0"
        // reads like there's more precision left when there isn't.
        if (mb >= 99.95) {
            return `${Math.round(mb)} MB`;
        }

        return `${mb.toFixed(1)} MB`;
    }

    const calculateStoragePercent = (bytes: number) => {
        const total = 100 * 1024 * 1024;
        const pct = (bytes / total) * 100;

        if (pct > 0 && pct <= 9) return 9; //show at least 9% otherwise its like its not even there
        return Math.min(100, Math.max(0, pct));
    };



    const changeColorThemeClick = (mode: string) => {
        localStorage.setItem("flashroom-color-theme", mode);
        setcTheme(mode);
    }

    const you = membersList.find((m) => m.clientId === clientId);

    return (

        <div className={`sidebar-cont ${sidebar ? 'sidebar-open' : 'sidebar-closed'}`}>
            <div id='side-bar-modes' className={` ${sidebar ? 'modes-open' : 'modes-closed'}`}>
                <div id='open-or-close' className='clickable' onClick={toggleSideBar}>
                    <svg className={`chev ${sidebar ? 'chev-open' : 'chev-closed'}`} viewBox="0 0 24 24" fill="none">
                        <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>
                {/* reflects whichever panel matches the current uiMode (Editor's file tree, or Drive's filters) — switching between Editor/Drive itself happens via UiModeToggle, not here */}
                <div className={`clickable sb-mode-btn ${(sbMode === 'editor' || sbMode === 'filelist') ? 'active-sb-mode-btn' : ''}`}
                    onClick={() => setModeClick(uiMode === 'files' ? 'filelist' : 'editor')}
                >
                    {/* {uiMode === 'files' ? (
                        <img className='no-select' src={`${process.env.PUBLIC_URL}/images/folder-icon-sidebar.png`} width='22px' alt='files' />
                    ) : (
                        <img className='no-select' src={`${process.env.PUBLIC_URL}/images/edit-icon-sb.png`} width='19px' alt='editor' />
                    )} */}

                    <img draggable={false} className='no-select' src={`${process.env.PUBLIC_URL}/images/folder-icon-sidebar.png`} width='22px' alt='files' />
                </div>

                {roomSettings.trackMembersEnabled && (
                    <div className={`clickable sb-mode-btn ${sbMode === 'members' ? 'active-sb-mode-btn' : ''}`}
                        onClick={() => setModeClick('members')}

                    >
                        <img draggable={false} className='no-select' src={`${process.env.PUBLIC_URL}/images/numusers2.png`} width='25px' alt='members' />
                    </div>
                )}

                {memberDetails?.role === 'owner' && (
                    <div className={`clickable sb-mode-btn ${sbMode === 'settings' ? 'active-sb-mode-btn' : ''}`}
                        onClick={() => setModeClick('settings')}

                    >
                        <img draggable={false} className='no-select' src={`${process.env.PUBLIC_URL}/images/settings.png`} width='22px' alt='settings' />
                    </div>
                )

                }


            </div>

            <div id='sidebar'
                className={` ${sidebar ? "sidebar-open" : 'sidebar-closed'}`}

            >


                <div
                    className='mobile-sheet-handle clickable'
                    onPointerDown={mobileSheetPointerDown}
                    role='button'
                    aria-label={sidebar ? 'Close sidebar' : 'Open sidebar'}
                >
                    <span />
                </div>


                <div id='sidebar-top'>


                    <div className='quick-flex logo-top'>

                        {ctheme === 'dark' ? (
                            <img draggable={false} className='no-select' src={`${process.env.PUBLIC_URL}/images/flashroom-logo-light.png`} alt='flashroom logo' />

                        ) : (
                            <img draggable={false} className='no-select' src={`${process.env.PUBLIC_URL}/images/flashroom-logo-dark.png`} alt='flashroom logo' />

                        )}

                        <h1 style={{ lineHeight: '1' }} className='pop no-select'>Flash<span>Room</span></h1>
                    </div>


                    {/* 
                    I don't think i will need this again. But Once I add accounts, i could make this the account icon
                    
                    <div className='center'>
                        <div className='center' id='profile-icon'>
                            <span>{localStorage.getItem('flashroom-accountName')?.slice(0, 1).toUpperCase()}</span>
                        </div>
                    </div> */}

                </div>

                {/* <div className='side-bar-tb'>

                </div> */}


                <div className='sidebar-info-row'>


                    <div className='sidebar-top-2'>

                        <div>

                            {
                                roomDetails ? (
                                    <div className='sidebar-ui-mode-slot'>
                                        <UiModeToggle />
                                    </div>
                                ) : (
                                    <h3 className='loading'>Loading...</h3>
                                )

                            }


                            {/* <div className='file-and-snip-count'>
                                                        <span>{Files.length} {Files.length === 1 ? 'File' : 'Files'} </span>
                                                    
                                                        <span>{Snippets.length} {Snippets.length === 1 ? 'Snippet' : 'Snippets'}</span>
                                                    </div> */}


                        </div>


                        <div className='sb-3' id='ws-people'>
                            <div className='people'>
                                {roomSettings.trackMembersEnabled ? (
                                    <>
                                        {ctheme === 'dark' ? (
                                            <img draggable={false} src={`${process.env.PUBLIC_URL}/images/numusers2.png`} alt='person icon' width='35px' />

                                        ) : (
                                            <img draggable={false} src={`${process.env.PUBLIC_URL}/images/numusers3.png`} alt='person icon' width='35px' />

                                        )}
                                        <span>{activeMembers.length}</span>
                                    </>
                                ) : (
                                    <div className='sidebar-views-pill' title={`${membersList.length} total view${membersList.length === 1 ? '' : 's'}`}>
                                        <ViewsIcon />
                                        <span>{formatCompactCount(membersList.length)}</span>
                                    </div>
                                )}
                            </div>
                        </div>



                    </div>



                </div>



                <div id='sidebar-inner'>
                    {sbMode === 'editor' ? (
                        <FileExplorerTab />
                    ) : sbMode === 'filelist' ? (
                        <FileListTab />
                    ) : sbMode === 'settings' ? (
                        <SettingsTab />
                    ) : (
                        <MemberTab />
                    )}

                </div>

                {/* <div id='sidebar-join-message'>
                    <h5>Go to Flash-Room.com/join and enter in code:</h5>
                    <div className='sjm-c'>
                        {roomDetails?.entranceCode.split("").map((l, index) => (
                            <React.Fragment key={index}>
                                <div className='sjm-l'>
                                    <p>{l}</p>
                                </div>

                                {(index === 2 || index === 4) && <span className="sjm-dash">-</span>}
                            </React.Fragment>
                        ))}
                    </div>

                </div> */}

                <div className='sb-cont'>
                    {roomDetails && (
                        <div className='storage-widget sidebar-top-storage'>
                            <div className='sw-top'>
                                <span className='s-lbl'>
                                    {calculateStorageInMb(roomStorage)} / 100 MB
                                </span>

                                {roomSettings.trackMembersEnabled && you && (
                                    <div
                                        className='syd-cell syd-cell-connected'
                                        aria-label={you.isActive ? 'Connected' : 'Disconnected'}
                                        title={you.isActive ? 'Connected' : 'Disconnected'}
                                    >
                                        <div className={`connected-indicator ${you.isActive ? 'active-connected' : 'inactive-connected'}`} />
                                        <span className='connected-label'>{you.isActive ? 'Connected' : 'Offline'}</span>
                                    </div>
                                )}
                            </div>
                            <div className='swb-o'>
                                <div style={{ width: `${calculateStoragePercent(roomStorage)}%` }} className='storage-widget-bar' />
                            </div>
                        </div>
                    )}

                </div>


                <div id='sidebar-bottom-cont'>
                    <div id='sbbc-i'>


                        <div className='sbc-row sbcr1'>

                            {memberDetails ? (
                                <div className='sidebar-your-details'>
                                    <div style={{ backgroundColor: you?.iconColor }} className='sbb-mi'>
                                        {memberDetails.name.slice(0, 1)}
                                    </div>

                                    {you &&
                                        <div className='sidebar-user-meta'>

                                            <div>
                                                {rename && (
                                                    <div className='sidebar-rename-popover'>
                                                        <MemberRenameInput
                                                            value={you?.name || ""}
                                                            isEditing={true}
                                                            onCancel={cancelRename}
                                                            onCommit={commitRename}
                                                        />
                                                    </div>
                                                )}

                                                <div onClick={() => setRename(true)} className="nam-edit clickable">
                                                    <p className="nam-lbl">{you?.name}</p>
                                                    {roomSettings.trackMembersEnabled && (
                                                        <div>
                                                            <RenameIcon />
                                                        </div>
                                                    )}
                                                </div>

                                                <div className='rl-cl'>
                                                    <span>{you.role}</span>
                                                </div>


                                            </div>

                                        </div>
                                    }
                                </div>
                            ) : (
                                <div className='sidebar-your-details sidebar-your-details-loading'>Loading ...</div>
                            )}

                        </div>


                        <div className='sbb-cm-btns'>
                            <div className={`dark-light quick-flex ${ctheme === 'dark' ? 'active-c-btn' : ''}`}
                                onClick={() => changeColorThemeClick("dark")}
                            >
                                <MoonIcon />
                                <span>Dark</span>
                            </div>

                            <div className={`dark-light quick-flex ${ctheme === 'light' ? 'active-c-btn' : ''}`}
                                onClick={() => changeColorThemeClick("light")}

                            >
                                <SunIcon />
                                <span>Light</span>
                            </div>
                        </div>

                    </div>


                </div>
            </div>


            <div className='mobile-bottom-bars'>

                <div className='mobile-join-row'>
                    <div className='mobile-join-row-left'>
                        <button
                            type='button'
                            className='mobile-open-menu-btn clickable'
                            onClick={toggleSideBar}
                            aria-label={sidebar ? 'Close menu' : 'Open menu'}
                            title={sidebar ? 'Close menu' : 'Open menu'}
                        >
                            <MenuIcon />
                        </button>

                        {roomDetails ? (
                            <button
                                type='button'
                                className='quick-flex top-bar-join primary-t-bg mobile-join join-code-trigger'
                                onClick={() => setQrPopup(true)}
                                title='Show room join details'
                            >
                                <JoinRoomIcon />

                                <div className='sjm-c'>
                                    {roomDetails?.entranceCode.split("").map((l, index) => (
                                        <React.Fragment key={index}>
                                            <div className='sjm-l'>
                                                <p>{l}</p>
                                            </div>

                                            {(index === 2 || index === 4) && <span className="sjm-dash">-</span>}
                                        </React.Fragment>
                                    ))}
                                </div>

                                <ExpandIcon />
                            </button>
                        ) : (
                            <h3 className='loading'>Loading...</h3>
                        )}
                    </div>

                    {memberDetails?.canCreate ? (
                        <div className='mobile-add-fab clickable' onClick={() => setAddNewPopup(true)}>
                            <PlusIcon />
                        </div>
                    ) : (
                        <div className='mobile-view-only-pill'>
                            <span>View Only</span>
                        </div>
                    )}
                </div>

                <div className='mobile-nav-row'>
                    <div
                        className={`mobile-nav-btn clickable ${sidebar && (sbMode === 'filelist' || sbMode === 'editor') ? 'active-mobile-nav' : ''}`}
                        onClick={() => mobileModeClick(uiMode === 'files' ? 'filelist' : 'editor')}
                    >
                        <img draggable={false} className='no-select' src={`${process.env.PUBLIC_URL}/images/folder-icon-sidebar.png`} alt='files' />
                        <span>Files</span>
                    </div>

                    {roomSettings.trackMembersEnabled && (
                        <div
                            className={`mobile-nav-btn clickable ${sidebar && sbMode === 'members' ? 'active-mobile-nav' : ''}`}
                            onClick={() => mobileModeClick('members')}
                        >
                            <img draggable={false} className='no-select' src={`${process.env.PUBLIC_URL}/images/numusers2.png`} alt='people' />
                            <span>People</span>
                        </div>
                    )}

                    {memberDetails?.role === 'owner' && (
                        <div
                            className={`mobile-nav-btn clickable ${sidebar && sbMode === 'settings' ? 'active-mobile-nav' : ''}`}
                            onClick={() => mobileModeClick('settings')}
                        >
                            <img draggable={false} className='no-select' src={`${process.env.PUBLIC_URL}/images/settings.png`} alt='settings' />
                            <span>Settings</span>
                        </div>
                    )}
                </div>

            </div>


        </div>
    )
}

export default SideBar
